// WARNING: this page is mostly AI slop
import React from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    styled,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShieldIcon from '@mui/icons-material/Shield';
import BarChartIcon from '@mui/icons-material/BarChart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { MainLayout } from 'layouts/MainLayout';
import { useSnackbar } from 'notistack';
import { EGAEventName, useGAHelpers } from 'lib/hooks/useGAHelper';

const PREFIX = 'ReviewsPage';

const classes = {
    container: `${PREFIX}-container`,
    heroSection: `${PREFIX}-heroSection`,
    tagline: `${PREFIX}-tagline`,
    headlineWrapper: `${PREFIX}-headlineWrapper`,
    headline: `${PREFIX}-headline`,
    googleLogo: `${PREFIX}-googleLogo`,
    subHeadline: `${PREFIX}-subHeadline`,
    featuredText: `${PREFIX}-featuredText`,
    bulletGrid: `${PREFIX}-bulletGrid`,
    bulletItem: `${PREFIX}-bulletItem`,
    checkmark: `${PREFIX}-checkmark`,
    button: `${PREFIX}-button`,
    benefitCard: `${PREFIX}-benefitCard`,
    iconWrapper: `${PREFIX}-iconWrapper`,
    featureCard: `${PREFIX}-featureCard`,
    featureList: `${PREFIX}-featureList`,
    featureItem: `${PREFIX}-featureItem`
};

const StyledContainer = styled(Box)(({ theme }) => ({
    maxWidth: 900,
    margin: '0 auto',
    padding: theme.spacing(2),
    [`& .${classes.heroSection}`]: {
        textAlign: 'center',
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(4)
    },
    [`& .${classes.tagline}`]: {
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color: theme.palette.secondary.main,
        fontWeight: 600,
        marginBottom: theme.spacing(2)
    },
    [`& .${classes.headlineWrapper}`]: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(0.5),
        flexWrap: 'wrap'
    },
    [`& .${classes.headline}`]: {
        fontWeight: 700,
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.75rem'
        }
    },
    [`& .${classes.googleLogo}`]: {
        width: 32,
        height: 32,
        [theme.breakpoints.up('md')]: {
            width: 40,
            height: 40
        }
    },
    [`& .${classes.button}`]: {
        marginTop: 1,
        marginBottom: 2,
        fontWeight: 700,
        textTransform: 'none',
        backgroundColor: theme.palette.secondary.main,
        borderColor: theme.palette.secondary.main,
        color: theme.palette.common.white,
        '&:hover': {
            backgroundColor: theme.palette.secondary.dark,
            borderColor: theme.palette.secondary.dark
        }
    },
    [`& .${classes.subHeadline}`]: {
        color: theme.palette.text.secondary,
        fontWeight: 500,
        marginBottom: theme.spacing(2)
    },
    [`& .${classes.featuredText}`]: {
        fontWeight: 500,
        maxWidth: 560,
        margin: '0 auto',
        marginBottom: theme.spacing(3)
    },
    [`& .${classes.bulletGrid}`]: {
        maxWidth: 500,
        margin: '0 auto',
        marginBottom: theme.spacing(3)
    },
    [`& .${classes.bulletItem}`]: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        textAlign: 'left',
        marginBottom: theme.spacing(1)
    },
    [`& .${classes.checkmark}`]: {
        color: theme.palette.secondary.main,
        fontWeight: 700,
        flexShrink: 0
    },
    [`& .${classes.benefitCard}`]: {
        height: '100%'
    },
    [`& .${classes.iconWrapper}`]: {
        width: 40,
        height: 40,
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.secondary.light,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: theme.spacing(1.5),
        '& svg': {
            color: theme.palette.secondary.main
        }
    },
    [`& .${classes.featureCard}`]: {
        backgroundColor: theme.palette.action.hover,
        height: '100%'
    },
    [`& .${classes.featureList}`]: {
        listStyle: 'none',
        padding: 0,
        margin: 0
    },
    [`& .${classes.featureItem}`]: {
        display: 'flex',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(2),
        '&:last-child': {
            marginBottom: 0
        }
    }
}));

const GoogleLogo = () => (
    <svg viewBox="0 0 24 24" style={{ width: 'inherit', height: 'inherit' }}>
        <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
    </svg>
);

const bulletPoints = [
    'Capture ratings automatically after every order',
    'Send 4 or 5 star customers straight to Google',
    'Keep constructive feedback private',
    'Track satisfaction trends with real-time analytics'
];

const benefits = [
    {
        icon: TrendingUpIcon,
        title: 'Boost Local SEO',
        description:
            'Direct 4-5 star ratings to Google to increase your visibility and trust with potential guests.'
    },
    {
        icon: ShieldIcon,
        title: 'Protect Your Brand',
        description: 'Negative feedback stays internal, allowing you to resolve issues privately.'
    },
    {
        icon: BarChartIcon,
        title: 'Actionable Insights',
        description: 'Link feedback to orders, see what people are saying, and monitor trends over time.'
    }
];

const opportunityFeatures = [
    {
        title: 'Drive Public Trust',
        description: 'Turn happy guests into Google reviews to boost footfall.'
    },
    {
        title: 'Smart Triage',
        description: 'Automatically send 4-5 star fans to Google while keeping other feedback private.'
    },
    {
        title: 'Connect Data to ROI',
        description: 'Link every rating to transactions to identify what drives success.'
    }
];

const operationalFeatures = [
    {
        title: 'Location-Specific URLs',
        description: 'Manage custom Google Review links for every venue.'
    },
    {
        title: 'Automated Cooldowns',
        description: 'Set frequency limits to ensure guests are never over-prompted.'
    },
    {
        title: 'Real-Time Analytics',
        description: 'Monitor satisfaction trends via your Pepper dashboard.'
    }
];

export const ReviewsPage: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { logUserEvent } = useGAHelpers();

    const handleTellMeMore = React.useCallback(() => {
        logUserEvent(EGAEventName.ReviewsPageIAmInterestedClick);
        enqueueSnackbar(
            'Thanks for your interest! The Pepper team will be in touch shortly to tell you more about Reviews.',
            {
                variant: 'success'
            }
        );
    }, [logUserEvent, enqueueSnackbar]);

    return (
        <MainLayout pageName="Reviews" pageDescription="Turn happy customers into 5-star Google reviews.">
            <StyledContainer>
                {/* Hero Section */}
                <Box className={classes.heroSection}>
                    <Typography variant="caption" className={classes.tagline}>
                        ✨ New Premium Feature
                    </Typography>

                    <Box className={classes.headlineWrapper}>
                        <Typography variant={isMobile ? 'h5' : 'h4'} className={classes.headline}>
                            Fill Your Google Page with 5 Stars
                        </Typography>
                        <Box className={classes.googleLogo}>
                            <GoogleLogo />
                        </Box>
                    </Box>

                    <Typography
                        variant={isMobile ? 'h6' : 'h5'}
                        className={classes.subHeadline}
                        sx={{ opacity: 0.8 }}
                    >
                        on autopilot
                    </Typography>

                    <Typography variant={isMobile ? 'body1' : 'h6'} className={classes.featuredText}>
                        Let us turn every happy customer into a 5-star Google review.
                    </Typography>

                    <Grid container spacing={1} className={classes.bulletGrid}>
                        {bulletPoints.map((point, index) => (
                            <Grid
                                key={index}
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}
                            >
                                <Box className={classes.bulletItem}>
                                    <span className={classes.checkmark}>&#10003;</span>
                                    <Typography variant="body2">{point}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={handleTellMeMore}
                        className={classes.button}
                    >
                        <Box sx={{ display: 'inline', mr: 1.5 }}>👋</Box>
                        {"I'm Interested"}
                    </Button>
                </Box>

                {/* Benefits Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {benefits.map((benefit, index) => (
                        <Grid
                            key={index}
                            size={{
                                xs: 12,
                                md: 4
                            }}
                        >
                            <Card className={classes.benefitCard} variant="outlined">
                                <CardContent>
                                    <Box className={classes.iconWrapper}>
                                        <benefit.icon />
                                    </Box>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                        {benefit.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {benefit.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Feature Lists */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 6
                        }}
                    >
                        <Card className={classes.featureCard} variant="outlined">
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                    The Opportunity
                                </Typography>
                                <ul className={classes.featureList}>
                                    {opportunityFeatures.map((feature, index) => (
                                        <li key={index} className={classes.featureItem}>
                                            <CheckCircleIcon
                                                fontSize="small"
                                                sx={{ mt: 0.25, color: 'secondary.main' }}
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                <strong style={{ color: theme.palette.text.primary }}>
                                                    {feature.title}:
                                                </strong>{' '}
                                                {feature.description}
                                            </Typography>
                                        </li>
                                    ))}
                                </ul>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 6
                        }}
                    >
                        <Card className={classes.featureCard} variant="outlined">
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                    Operational Control
                                </Typography>
                                <ul className={classes.featureList}>
                                    {operationalFeatures.map((feature, index) => (
                                        <li key={index} className={classes.featureItem}>
                                            <CheckCircleIcon
                                                fontSize="small"
                                                sx={{ mt: 0.25, color: 'secondary.main' }}
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                <strong style={{ color: theme.palette.text.primary }}>
                                                    {feature.title}:
                                                </strong>{' '}
                                                {feature.description}
                                            </Typography>
                                        </li>
                                    ))}
                                </ul>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </StyledContainer>
        </MainLayout>
    );
};
